// utils/redirectStorage.js
const REDIRECT_URL_KEY = "student-redirect-url";

/**
 * Checks if a URL is a valid student route
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if it's a valid student route
 */
const isValidStudentRoute = (url) => {
  if (!url || typeof url !== "string") return false;

  // Remove leading slash if present
  const cleanUrl = url.startsWith("/") ? url.slice(1) : url;

  // Check if it starts with student routes
  const studentRoutePatterns = [
    "student/myClassrooms",
    "student/profile",
    "notifications/list",
  ];

  return studentRoutePatterns.some((pattern) => cleanUrl.startsWith(pattern));
};

/**
 * Sanitizes URL to prevent XSS and injection attacks
 * @param {string} url - The URL to sanitize
 * @returns {string} - Sanitized URL
 */
const sanitizeUrl = (url) => {
  if (!url) return "";

  // Remove any potential script injections
  return url.replace(/[<>'"]/g, "").trim();
};

/**
 * Stores the redirect URL for student routes
 * @param {string} url - The URL to store
 * @returns {boolean} - True if stored successfully
 */
export const setStudentRedirectUrl = (url) => {
  try {
    if (!isValidStudentRoute(url)) {
      return false;
    }

    const sanitizedUrl = sanitizeUrl(url);
    if (!sanitizedUrl) {
      return false;
    }

    sessionStorage.setItem(REDIRECT_URL_KEY, sanitizedUrl);

    // Verify it was stored
    // const stored = sessionStorage.getItem(REDIRECT_URL_KEY);
    // console.log("✅ Verification - Retrieved from storage:", stored);
    return true;
  } catch (error) {
    console.error("❌ Error storing redirect URL:", error);
    return false;
  }
};

/**
 * Gets the stored redirect URL for students
 * @returns {string|null} - The stored URL or null if none exists
 */
export const getStudentRedirectUrl = () => {
  try {
    const url = sessionStorage.getItem(REDIRECT_URL_KEY);

    if (!url) return null;

    // Double-check the URL is still valid
    if (!isValidStudentRoute(url)) {
      clearStudentRedirectUrl();
      return null;
    }

    return url;
  } catch (error) {
    console.error("Error retrieving redirect URL:", error);
    return null;
  }
};

/**
 * Clears the stored redirect URL
 */
export const clearStudentRedirectUrl = () => {
  try {
    sessionStorage.removeItem(REDIRECT_URL_KEY);
  } catch (error) {
    console.error("Error clearing redirect URL:", error);
  }
};

/**
 * Extracts classroomId from a student URL
 * @param {string} url - The student URL
 * @returns {string|null} - The extracted classroomId or null
 */
export const extractClassroomIdFromUrl = (url) => {
  if (!url) return null;

  // Pattern: /student/myClassrooms/:classroomId/...
  const match = url.match(/\/student\/myClassrooms\/([^\/]+)/);
  return match ? match[1] : null;
};

/**
 * Gets the classroomId from stored redirect URL
 * @returns {string|null} - The classroomId or null if none exists
 */
export const getClassroomIdFromStoredUrl = () => {
  const url = getStudentRedirectUrl();
  return extractClassroomIdFromUrl(url);
};

/**
 * Gets the redirect URL and immediately clears it
 * @returns {string|null} - The stored URL or null if none exists
 */
export const consumeStudentRedirectUrl = () => {
  const url = getStudentRedirectUrl();
  if (url) {
    clearStudentRedirectUrl();
  } else {
    // console.log("🔄 consumeStudentRedirectUrl - No URL found in storage");
  }
  return url;
};
